/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {defer, finalize, Observable, of, shareReplay, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';

interface InFlightImageEntry {
    fetch$: Observable<string>;
}

@Injectable({
    providedIn: 'root'
})
export class FileDataImageCacheService {
    /** Idle blob URLs retained for quick remount; evicted LRU when this limit is exceeded. */
    private static readonly MAX_IDLE_CACHE_ENTRIES = 100;

    private readonly cache = new Map<number, string>();
    private readonly refCounts = new Map<number, number>();
    private readonly idleReleasedAt = new Map<number, number>();
    private readonly inFlight = new Map<number, InFlightImageEntry>();

    constructor(private http: HttpClient) {}

    getDisplayUrl(httpUrl: string): Observable<string> {
        const fileDataId = FileDataImageCacheService.parseFileDataId(httpUrl);
        if (fileDataId == null) {
            return this.fetchUncached(httpUrl);
        }

        const cachedObjectUrl = this.cache.get(fileDataId);
        if (cachedObjectUrl) {
            return this.trackUsage(fileDataId, of(cachedObjectUrl));
        }

        const pending = this.inFlight.get(fileDataId);
        if (pending) {
            return this.trackUsage(fileDataId, pending.fetch$);
        }

        const fetch$ = this.http.get(httpUrl, {responseType: 'blob'}).pipe(
            map((blob: Blob) => {
                const objectUrl = URL.createObjectURL(blob);
                this.cache.set(fileDataId, objectUrl);
                this.inFlight.delete(fileDataId);
                if (!this.refCounts.has(fileDataId)) {
                    this.idleReleasedAt.set(fileDataId, Date.now());
                }
                this.evictIdleEntriesIfNeeded();
                return objectUrl;
            }),
            shareReplay(1),
            catchError((error) => {
                this.inFlight.delete(fileDataId);
                return throwError(() => error);
            })
        );

        this.inFlight.set(fileDataId, {fetch$});
        return this.trackUsage(fileDataId, fetch$);
    }

    /** Decrements active usage; idle entries are retained briefly then LRU-evicted. */
    release(fileDataId: number): void {
        const count = (this.refCounts.get(fileDataId) ?? 0) - 1;
        if (count <= 0) {
            this.refCounts.delete(fileDataId);
            if (this.cache.has(fileDataId)) {
                this.idleReleasedAt.set(fileDataId, Date.now());
                this.evictIdleEntriesIfNeeded();
            }
        } else {
            this.refCounts.set(fileDataId, count);
        }
    }

    clear(): void {
        for (const objectUrl of this.cache.values()) {
            URL.revokeObjectURL(objectUrl);
        }
        this.cache.clear();
        this.refCounts.clear();
        this.idleReleasedAt.clear();
        this.inFlight.clear();
    }

    static parseFileDataId(httpUrl: string): number | null {
        try {
            const pathname = new URL(httpUrl).pathname;
            const match = pathname.match(/(\d+)(?:\.\w+)?$/);
            if (!match) {
                return null;
            }
            const fileDataId = Number.parseInt(match[1], 10);
            return Number.isNaN(fileDataId) ? null : fileDataId;
        } catch {
            return null;
        }
    }

    private trackUsage(fileDataId: number, source$: Observable<string>): Observable<string> {
        return defer(() => {
            this.acquire(fileDataId);
            // Keep the subscription open so the object URL is treated as "in use" until callers unsubscribe.
            return new Observable<string>((subscriber) => {
                const subscription = source$.subscribe({
                    next: (value) => subscriber.next(value),
                    error: (error) => subscriber.error(error),
                    complete: () => {
                        // Intentionally do not complete; release happens on unsubscribe via finalize().
                    }
                });
                return () => subscription.unsubscribe();
            });
        }).pipe(finalize(() => this.release(fileDataId)));
    }

    private acquire(fileDataId: number): void {
        this.refCounts.set(fileDataId, (this.refCounts.get(fileDataId) ?? 0) + 1);
        this.idleReleasedAt.delete(fileDataId);
    }

    private evictIdleEntriesIfNeeded(): void {
        const idleIds = [...this.cache.keys()].filter((id) => !this.refCounts.has(id));
        const excessIdleCount = idleIds.length - FileDataImageCacheService.MAX_IDLE_CACHE_ENTRIES;
        if (excessIdleCount <= 0) {
            return;
        }
        idleIds.sort((a, b) => (this.idleReleasedAt.get(a) ?? 0) - (this.idleReleasedAt.get(b) ?? 0))
               .slice(0, excessIdleCount)
               .forEach((fileDataId) => this.removeCacheEntry(fileDataId));
    }

    private removeCacheEntry(fileDataId: number): void {
        const objectUrl = this.cache.get(fileDataId);
        if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
        }
        this.cache.delete(fileDataId);
        this.idleReleasedAt.delete(fileDataId);
    }

    private fetchUncached(httpUrl: string): Observable<string> {
        return this.http.get(httpUrl, {responseType: 'blob'}).pipe(
            map((blob: Blob) => URL.createObjectURL(blob))
        );
    }
}
